import { createElement, Fragment, type ReactNode, useMemo } from 'react'
import { parse } from '@/minidown/parser'
import { type MinidownNode, MinidownNodeType } from '@/minidown/nodes'

interface MinidownContentProps {
  /**
   * The Minidown source to render.
   */
  value: string
}

/**
 * Renders Minidown as React components.
 */
export function MinidownContent({ value }: MinidownContentProps) {
  const root = useMemo<MinidownNode>(() => {
    try {
      return parse(value)
    } catch (err) {
      console.error(
        `Unable to parse Minidown. The Minidown source is:\n\n${value}`,
        err,
      )
      // Return the Minidown source as-is if we can't render it.
      return {
        type: MinidownNodeType.Root,
        children: [{ type: MinidownNodeType.Text, value }],
      }
    }
  }, [value])
  return createElement(Fragment, {}, ...visit(root))
}

/**
 * Renders a React node for each AST node.
 */
function visit(node: MinidownNode): ReactNode[] {
  switch (node.type) {
    case MinidownNodeType.Root:
      return node.children.flatMap(visit)
    case MinidownNodeType.Text:
      return [node.value]
    case MinidownNodeType.Emphasis:
      return [createElement('em', {}, ...node.children.flatMap(visit))]
    case MinidownNodeType.Strong:
      return [createElement('strong', {}, ...node.children.flatMap(visit))]

    default:
      return []
  }
}
